import React, { useState } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import SignUp from './components/SignUp';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import ConfirmEmail from './components/ConfirmEmail';
import ProtectedRoute from './components/ProtectedRoute';
import Navbar from './components/Navbar';
import About from './components/About';
import Contact from './components/Contact';
import Onboarding from './components/Onboarding';
import ModulesList from './components/ModulesList';
import ActivityPage from './components/ActivityPage'; // New ActivityPage component
import CaseStudies from './components/CaseStudies';   // New CaseStudies component
import Pricing from './components/Pricing';           // New Pricing component
import PasswordReset from './components/PasswordReset';
import UpdatePassword from './components/UpdatePassword';
import ErrorBoundary from './components/ErrorBoundary';
import AccountSettings from './components/AccountSettings';
import Assistant from './components/Assistant';
import MyCalendar from './components/Calendar';
import TestSection from './components/TestSection';
import Home from './components/Home'; // Home component for logged-out users

import supabase from './supabase';

const App = () => {
  const [currentThread, setCurrentThread] = useState(null);

  const handleOnboardingSubmit = async (formData) => {
    try {
      const { data: { session }, error: userError } = await supabase.auth.getSession();
      const user = session?.user;
      if (userError || !user) {
        throw new Error(userError?.message || "No user found.");
      }

      const payload = {
        is_onboarded: true,
        has_existing_business: formData.has_existing_business,
        business_name: formData.business_name,
        business_website: formData.business_website || null,
        business_start_date: formData.business_start_date || null,
        business_age_years: formData.business_age_years || null,
        platforms_used: formData.platforms_used || [],
        marketing_tools_used: formData.marketing_tools_used || [],
        platform_profiles: formData.platform_profiles || {},
      };

      const { error: updateError } = await supabase
        .from("profiles")
        .update(payload)
        .eq("user_id", user.id);

      if (updateError) {
        throw new Error(updateError.message);
      }

      console.log("Profile updated successfully");
    } catch (err) {
      console.error("Error submitting onboarding data:", err.message);
    }
  };

  return (
    <ErrorBoundary>
      <DndProvider backend={HTML5Backend}>
        <Router>
          <Navbar currentThread={currentThread} />
          <div className="container">
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<SignUp />} />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard
                      setCurrentThread={setCurrentThread}
                      currentThread={currentThread}
                    />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/modules"
                element={
                  <ProtectedRoute>
                    <ModulesList />
                  </ProtectedRoute>
                }
              />
              <Route path="/confirm-email" element={<ConfirmEmail />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/onboarding" element={<Onboarding onSubmit={handleOnboardingSubmit} />} />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <AccountSettings />
                  </ProtectedRoute>
                }
              />
              <Route path="/assistant" element={<Assistant />} />
              <Route
                path="/calendar"
                element={
                  <ProtectedRoute>
                    <MyCalendar />
                  </ProtectedRoute>
                }
              />
              <Route path="/password-reset" element={<PasswordReset />} />
              <Route path="/update-password" element={<UpdatePassword />} />
              <Route path="/test" element={<TestSection />} />
              {/* New routes for Case Studies and Pricing pages */}
              <Route path="/case-studies" element={<CaseStudies />} />
              <Route path="/pricing" element={<Pricing />} />
              {/* New route for Activity pages */}
              <Route path="/activity/:pageUrl" element={<ActivityPage />} />
              <Route path="*" element={<h2>404 - Page Not Found</h2>} />
            </Routes>
          </div>
        </Router>
      </DndProvider>
    </ErrorBoundary>
  );
};

export default App;
